/**
 * Critical Lambda health table.
 *
 * Backend shape:
 *   Array<{
 *     name: string,
 *     invocations_1h: number,
 *     errors_1h: number,
 *     avg_duration_ms: number | null,
 *     status: 'ok' | 'unavailable',
 *     error: string | null,
 *   }>
 *
 * 2026-08-21 (U29): a row whose CloudWatch read was denied used to render as
 * 0 invocations / 0 errors, which is indistinguishable from a quiet Lambda.
 * An unreadable row now shows em-dashes and says so.
 *
 * CRITICAL_LAMBDAS list lives in backend/src/routes/admin-system-health.ts.
 * The table renders rows in backend-supplied order (no client-side sort
 * to keep the operational hierarchy explicit).
 */
import { formatNumber } from '@/lib/utils';
import { TileMeta } from './TileStatus';

interface LambdaRow extends TileMeta {
  name: string;
  invocations_1h: number;
  errors_1h: number;
  avg_duration_ms: number | null;
}

export function LambdaHealthCard({ data }: { data: LambdaRow[] }) {
  const rows = data ?? [];
  return (
    <section className="bg-card border rounded-lg p-6 overflow-x-auto">
      <h3 className="text-lg font-semibold mb-1">Critical Lambdas</h3>
      <p className="text-xs text-muted-foreground mb-4">Invocations + errors in the last 1h.</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            <th className="py-2 pr-3 font-medium">Lambda</th>
            <th className="py-2 pr-3 font-medium text-right">Invocations (1h)</th>
            <th className="py-2 pr-3 font-medium text-right">Errors (1h)</th>
            <th className="py-2 pr-3 font-medium text-right">Avg duration</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="py-3 text-sm text-muted-foreground">
                No Lambda metric data available.
              </td>
            </tr>
          ) : (
            rows.map((l) => {
              const down = l.status === 'unavailable';
              return (
                <tr key={l.name} className={`border-b last:border-0 ${down ? 'bg-amber-500/5' : ''}`}>
                  <td className="py-2 pr-3 font-mono text-xs">
                    {l.name}
                    {down && (
                      <span className="ml-2 font-sans text-[10px] uppercase tracking-wide text-amber-700">
                        unavailable
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-mono text-right">
                    {down ? '—' : formatNumber(l.invocations_1h ?? 0)}
                  </td>
                  <td
                    className={`py-2 pr-3 font-mono text-right ${
                      !down && (l.errors_1h ?? 0) > 0 ? 'text-red-600 font-semibold' : ''
                    }`}
                  >
                    {down ? '—' : formatNumber(l.errors_1h ?? 0)}
                  </td>
                  <td className="py-2 pr-3 font-mono text-right">
                    {down || l.avg_duration_ms == null
                      ? '—'
                      : `${Math.round(l.avg_duration_ms)}ms`}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </section>
  );
}
